/* Created by Interactive 63 - https://interactive63.com */
/**
 * The configuration the JavaScript runtime reads from an exported scene, and the safe way to
 * place it inside an HTML `<script>` element.
 */
import type { MorphTrackData } from '../morph/build';
import type { ExportSettings, Project, SceneSettings, Trigger } from '../model/types';

export interface RuntimeConfig {
  v: 1;
  /** The scene name, without the "st-" prefix. */
  scene: string;
  timing: {
    duration: number;
    delay: number;
    /** null means infinite, because JSON has no Infinity. */
    iterations: number | null;
    direction: SceneSettings['direction'];
    fill: SceneSettings['fillMode'];
  };
  trigger: Trigger;
  reducedMotion: SceneSettings['reducedMotion'];
  /** Shape morphs, normalised and ready to play. */
  morphs: MorphTrackData[];
}

/** Builds the runtime config for a project. `iterations` overrides the scene's repeat count. */
export function buildRuntimeConfig(
  project: Project,
  settings: ExportSettings,
  iterations?: number | 'infinite',
  morphs: MorphTrackData[] = [],
): RuntimeConfig {
  const { scene } = project;
  const repeat = iterations ?? scene.iterations;
  return {
    v: 1,
    scene: settings.sceneName || 'scene',
    timing: {
      duration: scene.duration,
      delay: scene.delay,
      iterations: repeat === 'infinite' ? null : repeat,
      direction: scene.direction,
      fill: scene.fillMode,
    },
    trigger: scene.trigger,
    reducedMotion: scene.reducedMotion,
    morphs,
  };
}

/**
 * Serialises a config for a `<script type="application/json">` element. The characters `<`, `>`
 * and `&` (and the line separators JavaScript treats as newlines) are written as unicode
 * escapes, so text such as `</script>` inside a label can never end the element early.
 */
export function serialiseConfigForHtml(config: RuntimeConfig): string {
  return JSON.stringify(config)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}
